import Client from './KomodoClient';

// komodo S3StorageController. Files up to SERVER_UPLOAD_MAX go through api/s3-storage/upload (the
// API puts the object, like Blazor does in-process). Larger files use the browser-direct multipart
// session (api/s3-storage/multipart, presigned part URLs), which needs the bucket CORS to allow the
// portal origin. Sessions live in the API process memory (24h) and part URLs for 1h.
export const PART_SIZE = 5 * 1024 * 1024; // the minimum komodo accepts
export const SERVER_UPLOAD_MAX = 5 * 10 * 1024 * 1024; // AppConstants.FileMaxAllowedSize * 5
const BATCH_SIZE = 24;
const enc = encodeURIComponent;

const uploadForm = (file, { purpose, keyPrefix, machineID, onProgress }) => {
	const form = new FormData();
	form.append('file', file, file.name);
	form.append('purpose', purpose || 'filerelation');
	form.append('keyPrefix', keyPrefix || 'multipart');
	form.append('machineID', machineID || 'portal');
	return Client.post('/api/s3-storage/upload', form, undefined, {
		headers: { 'Content-Type': 'multipart/form-data' },
		onUploadProgress: (e) => {
			if (onProgress) onProgress(e.loaded, e.total || file.size);
		},
	});
};

const createSession = (req) =>
	Client.post('/api/s3-storage/multipart/sessions', req);
const partUrls = (uploadId, startPartNumber, count) =>
	Client.post(`/api/s3-storage/multipart/sessions/${enc(uploadId)}/part-urls`, {
		startPartNumber,
		count,
	});
const complete = (uploadId, parts) =>
	Client.post(`/api/s3-storage/multipart/sessions/${enc(uploadId)}/complete`, {
		parts,
	});
const abort = (uploadId) =>
	Client.post(`/api/s3-storage/multipart/sessions/${enc(uploadId)}/abort`);

// PUT one part straight to S3. No komodo headers, no content-type (the presign covers none); the
// bucket CORS must expose ETag.
const putPart = (url, blob, onProgress) =>
	new Promise((resolve, reject) => {
		const xhr = new XMLHttpRequest();
		xhr.open('PUT', url, true);
		xhr.upload.onprogress = (e) => {
			if (onProgress && e.lengthComputable) onProgress(e.loaded);
		};
		xhr.onload = () => {
			if (xhr.status >= 200 && xhr.status < 300) {
				const eTag = xhr.getResponseHeader('ETag');
				if (!eTag)
					reject(new Error('S3 did not expose the ETag header (bucket CORS).'));
				else resolve(eTag);
			} else {
				reject(new Error(`S3 part upload failed (${xhr.status})`));
			}
		};
		xhr.onerror = () => reject(new Error('S3 part upload failed (network)'));
		xhr.send(blob);
	});

/**
 * Upload a File and return { key, bucket, eTag, location } (eTag / location only for multipart).
 * onProgress(uploadedBytes, totalBytes).
 */
const uploadFile = async (file, options) => {
	const { purpose, keyPrefix, machineID, onProgress } = options || {};
	if (file.size <= SERVER_UPLOAD_MAX) {
		return uploadForm(file, { purpose, keyPrefix, machineID, onProgress });
	}
	const session = await createSession({
		purpose: purpose || 'filerelation',
		fileName: file.name,
		contentType: file.type || 'application/octet-stream',
		sizeBytes: file.size,
		partSizeBytes: PART_SIZE,
		batchSize: BATCH_SIZE,
		keyPrefix: keyPrefix || 'multipart',
		machineID: machineID || 'portal',
	});
	const { uploadId, totalParts, partSizeBytes } = session;
	const urls = {};
	(session.parts || []).forEach((p) => {
		urls[p.partNumber] = p.url;
	});
	const done = [];
	let uploaded = 0;
	try {
		for (let n = 1; n <= totalParts; n += 1) {
			if (!urls[n]) {
				// eslint-disable-next-line no-await-in-loop
				const more = await partUrls(uploadId, n, BATCH_SIZE);
				(more.parts || []).forEach((p) => {
					urls[p.partNumber] = p.url;
				});
			}
			const start = (n - 1) * partSizeBytes;
			const blob = file.slice(
				start,
				Math.min(start + partSizeBytes, file.size)
			);
			const base = uploaded;
			// eslint-disable-next-line no-await-in-loop
			const eTag = await putPart(urls[n], blob, (loaded) => {
				if (onProgress) onProgress(base + loaded, file.size);
			});
			uploaded += blob.size;
			if (onProgress) onProgress(uploaded, file.size);
			done.push({ partNumber: n, eTag });
		}
		return complete(uploadId, done);
	} catch (err) {
		try {
			await abort(uploadId);
		} catch (e) {
			// the session may already be gone; the original error matters
		}
		throw err;
	}
};

export default {
	PART_SIZE,
	SERVER_UPLOAD_MAX,
	createSession,
	partUrls,
	complete,
	abort,
	uploadFile,
};
